import { useState } from 'react';
import { mergeOrders, type AdminOrderRow } from '../api/orders';
import { formatGroszeAsZl } from '../util/money';

type Props = {
  selected: AdminOrderRow[];
  onRemove: (id: string) => void;
  onClose: () => void;
  onMerged: () => void | Promise<void>;
};

export function MergeModal({ selected, onRemove, onClose, onMerged }: Props) {
  const [mainId, setMainId] = useState<string>(selected[0]?.id ?? '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverMismatch, setServerMismatch] = useState(false);

  // The main order keeps the parcel, so it has to be one of the orders still in the list after
  // the operator drops any of them.
  const effectiveMainId = selected.some((o) => o.id === mainId) ? mainId : (selected[0]?.id ?? '');
  const main = selected.find((o) => o.id === effectiveMainId);

  // The parcel travels on the main's shipping (CONTEXT.md → Merge), so picking the main is picking
  // the shipping. The API refuses differing shipping unless forced, and force is sent only once
  // the operator has been shown which shipping stays.
  const mixedShipping =
    serverMismatch || (main !== undefined && selected.some((o) => !sameShipping(o, main)));

  const pickMain = (id: string) => {
    setMainId(id);
    setServerMismatch(false);
  };

  const submit = async () => {
    if (!effectiveMainId || selected.length < 2) return;
    setSubmitting(true);
    setError(null);
    try {
      const others = selected.map((o) => o.id).filter((id) => id !== effectiveMainId);
      await mergeOrders(effectiveMainId, others, note.trim() || undefined, mixedShipping || undefined);
      await onMerged();
      onClose();
    } catch (err) {
      if (errorCode(err) === 'address_mismatch') setServerMismatch(true);
      setError(mergeErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const sumGrosze = selected.reduce((s, o) => s + o.totalGrosze, 0);
  const shirtCount = selected.reduce((s, o) => s + o.items.reduce((a, i) => a + i.qty, 0), 0);

  return (
    <ModalShell onClose={onClose} width={720}>
      <ModalHead title="Połącz w jedną paczkę" subtitle="OPERACJA" onClose={onClose} />

      <div style={{ padding: 22 }}>
        <p style={{ margin: '0 0 10px', fontSize: 13 }}>
          Wybierz zamówienie główne · paczka pojedzie <b>jego wysyłką</b>.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selected.map((o) => {
            const isMain = o.id === effectiveMainId;
            const losesShipping = !isMain && main !== undefined && !sameShipping(o, main);
            return (
              <label
                key={o.id}
                style={{
                  border: isMain ? '2px solid var(--orange)' : '1px solid var(--admin-line)',
                  background: isMain ? '#FFF6EC' : 'var(--admin-card)',
                  padding: 12,
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: 12,
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="main"
                  checked={isMain}
                  onChange={() => pickMain(o.id)}
                />
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="mono" style={{ fontWeight: 700 }}>
                      {o.id}
                    </span>
                    <span>· {o.customerName}</span>
                    {isMain && (
                      <span
                        className="pill"
                        style={{ background: 'var(--orange)', color: 'var(--ink)' }}
                      >
                        GŁÓWNE · TA WYSYŁKA
                      </span>
                    )}
                  </div>
                  <div
                    className="mono"
                    style={{ fontSize: 12, color: 'var(--admin-muted)', marginTop: 2 }}
                  >
                    {o.items
                      .map((i) => `${i.cut} · ${i.size}${i.qty > 1 ? ` × ${i.qty}` : ''}`)
                      .join(' + ')}{' '}
                    ·{' '}
                    <span
                      style={losesShipping ? { textDecoration: 'line-through' } : undefined}
                      title={losesShipping ? 'Ta wysyłka nie zostanie użyta' : undefined}
                    >
                      {shippingShort(o)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-a"
                  aria-label={`Usuń ${o.id} z łączenia`}
                  style={{ borderColor: 'transparent', color: 'var(--admin-muted)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    onRemove(o.id);
                  }}
                >
                  ✕
                </button>
              </label>
            );
          })}
        </div>

        {main && (
          <div
            role="status"
            style={{
              marginTop: 14,
              padding: '8px 12px',
              background: '#FFFAF0',
              border: '1px solid #F0D9B8',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 12,
            }}
          >
            <span style={{ fontSize: 14 }}>{mixedShipping ? '⚠' : '✓'}</span>
            {mixedShipping ? (
              <span>
                Różne sposoby wysyłki · paczka pojedzie wysyłką zamówienia{' '}
                <b className="mono">{main.id}</b>: <b>{shippingShort(main)}</b>. Koszt wysyłki
                pozostałych nie wraca automatycznie.
              </span>
            ) : (
              <span>
                Ta sama wysyłka: <b>{shippingShort(main)}</b>
              </span>
            )}
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 10,
              padding: '8px 12px',
              border: '1px solid #C9483A',
              color: '#C9483A',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div className="field" style={{ marginTop: 14 }}>
          <label
            htmlFor="merge-note"
            style={{ color: 'var(--admin-ink)', fontSize: 11, fontFamily: 'var(--display)' }}
          >
            Notatka wewnętrzna (opcj.)
          </label>
          <input
            id="merge-note"
            className="input-a"
            style={{ width: '100%', marginTop: 4 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>

      <ModalFoot
        left={
          <span className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
            SUMA POŁĄCZONA:{' '}
            <b style={{ color: 'var(--admin-ink)' }}>{formatGroszeAsZl(sumGrosze)}</b> ·{' '}
            {shirtCount} koszul.
          </span>
        }
      >
        <button className="btn-a" onClick={onClose} disabled={submitting}>
          Zamknij
        </button>
        <button
          className="btn-a primary"
          onClick={submit}
          disabled={submitting || selected.length < 2}
        >
          ⊕ Połącz zamówienia
        </button>
      </ModalFoot>
    </ModalShell>
  );
}

function sameShipping(a: AdminOrderRow, b: AdminOrderRow): boolean {
  return (
    a.shippingMethod === b.shippingMethod &&
    JSON.stringify(a.shippingAddress) === JSON.stringify(b.shippingAddress)
  );
}

function errorCode(err: unknown): string | undefined {
  return (err as { body?: { error?: string } }).body?.error;
}

function mergeErrorMessage(err: unknown): string {
  const body = (err as { body?: { error?: string; id?: string; mismatches?: string[] } }).body;
  switch (body?.error) {
    case 'address_mismatch': {
      const which = body.mismatches?.length ? ` (${body.mismatches.join(', ')})` : '';
      return `Adres wysyłki zmienił się w międzyczasie${which} · sprawdź, która wysyłka zostaje, i połącz ponownie.`;
    }
    case 'must_be_paid':
      return `Tylko opłacone zamówienia można łączyć${body.id ? ` (${body.id})` : ''}.`;
    case 'already_merged':
      return `Zamówienie${body.id ? ` ${body.id}` : ''} jest już częścią innej paczki.`;
    case 'some_not_found':
      return 'Części zaznaczonych zamówień już nie ma · odśwież listę.';
    default:
      return 'Nie udało się połączyć zamówień.';
  }
}

function shippingShort(o: AdminOrderRow) {
  const addr = o.shippingAddress;
  if (o.shippingMethod === 'paczkomat') return `Paczkomat · ${addr.paczkomat?.code ?? ''}`;
  if (o.shippingMethod === 'kurier')
    return `Kurier · ${addr.courier?.city ?? ''}, ${addr.courier?.street ?? ''}`;
  if (o.shippingMethod === 'zagranica') {
    return `Zagranica · ${addr.international?.countryName ?? addr.international?.country ?? ''}`;
  }
  return 'Odbiór osobisty';
}

// ---- shared modal shell -------------------------------------------------

export function ModalShell({
  onClose,
  width,
  children,
}: {
  onClose: () => void;
  width: number;
  children: import('react').ReactNode;
}) {
  return (
    <div className="a-modal-overlay" onClick={onClose}>
      <div
        className="card a-modal"
        role="dialog"
        aria-modal="true"
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalHead({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
}) {
  return (
    <div className="a-modal-head">
      <div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
          {subtitle}
        </div>
        <h1 style={{ marginTop: 4 }}>{title}</h1>
      </div>
      <button className="btn-a" style={{ borderColor: 'transparent' }} onClick={onClose}>
        ✕
      </button>
    </div>
  );
}

export function ModalFoot({
  left,
  children,
}: {
  left?: import('react').ReactNode;
  children: import('react').ReactNode;
}) {
  return (
    <div className="a-modal-foot">
      <div>{left}</div>
      <div className="a-modal-actions">{children}</div>
    </div>
  );
}
